import type { JSONObject } from '@segment/analytics-next/dist/types/core/events';
import type { ComponentType } from 'react';
import BadgeList from 'src/components/badge-list';

import {
  matchToArrayField,
  resolveDisplayValues,
} from '@/utils/table/utils/customAttributeHelpers';

import { CustomAttributeMultiSelect } from '../renderers/field-layouts/CustomAttributeMultiSelect';
import type { CustomAttributeProps } from '../renderers/field-layouts/CustomAttributeProps';
import { getBasicFieldConfig } from './defaults';
import { type FieldTypeConfig } from './types';

// Case-insensitive substring match against any string element of a multi-select
// row value. Mirrors the server-side `ILIKE '%value%'` semantics used by the
// reporting layer (sqlQueryBuilder) so that `:` / `!:` behave identically across
// list tables, dashboard widgets, and reports (RSP-3765 AC7).
const hasSubstringMatch = (
  rowValues: unknown,
  filterValue: string
): boolean => {
  if (!Array.isArray(rowValues)) {
    return false;
  }
  const needle = filterValue.toLowerCase();

  return rowValues.some(
    (v) => typeof v === 'string' && v.toLowerCase().includes(needle)
  );
};

export const multiselect: FieldTypeConfig = {
  i18nKey: 'customAttributes.fieldTypes.multiselect',
  FieldComponent:
    // TODO: Support generic value type
    CustomAttributeMultiSelect as unknown as ComponentType<CustomAttributeProps>,
  getConditionalPropertyFilterProperty: () => ({
    defaultOperator: ':',
    operators: [
      // Condition builder operators keep exact-token matching because users
      // pick from the known-options dropdown — `:` here means "selection
      // includes this token". Table / widget filters use substring matching;
      // the same symbol carries different semantics in the two surfaces.
      {
        operator: ':',
        match: (rowValues: unknown, filterValue: string) =>
          Array.isArray(rowValues) && rowValues.includes(filterValue),
      },
      {
        operator: '!:',
        match: (rowValues: unknown, filterValue: string) =>
          !Array.isArray(rowValues) || !rowValues.includes(filterValue),
      },
    ],
  }),
  getConditionalPropertyFilterOptions: (renderProps) => {
    return (
      renderProps.options?.map((option) => {
        if (option._tag === 'StringOption') {
          return { value: option.Value, label: option.Value };
        } else {
          return { value: option.AltValue, label: option.Value };
        }
      }) ?? []
    );
  },
  getTableFieldConfig: (renderProps, options) => {
    const displayValues = resolveDisplayValues({
      formFieldOptions: renderProps.options,
      useAlternativeValues: options?.useAlternateValues ?? false,
    });

    return {
      ...getBasicFieldConfig(renderProps, {
        useAlternateValues: !!options.useAlternateValues,
      }),
      filterOptions: {
        filteringProperties: {
          operators: [
            {
              operator: '=',
              match: (rowValues: unknown, filterValue: string) =>
                Array.isArray(rowValues) && rowValues.includes(filterValue),
            },
            {
              operator: ':',
              match: hasSubstringMatch,
            },
            {
              operator: '!=',
              match: (rowValues: unknown, filterValue: string) =>
                !Array.isArray(rowValues) || !rowValues.includes(filterValue),
            },
            {
              operator: '!:',
              // Empty array and null/undefined rows both pass (AC4). `!hasSubstringMatch`
              // already covers the empty-array case (`[].some === false`), but
              // we surface the empty-array branch explicitly for readability
              // and parity with the server-side `jsonb_array_length = 0` arm.
              match: (rowValues: unknown, filterValue: string) =>
                !Array.isArray(rowValues) ||
                rowValues.length === 0 ||
                !hasSubstringMatch(rowValues, filterValue),
            },
          ],
        },
        filteringOptions:
          renderProps.options?.map((value) => {
            if (value._tag === 'AltValueOption' && options.useAlternateValues) {
              return { value: value.AltValue, label: value.AltValue };
            } else {
              return { value: value.Value, label: value.Value };
            }
          }) ?? [],
      },

      customFieldValue: (item: { CustomAttributeData: JSONObject }) => {
        return matchToArrayField(
          item.CustomAttributeData,
          renderProps.path,
          displayValues
        );
      },

      cell: (data) => {
        return (
          <BadgeList
            badges={matchToArrayField(
              data.CustomAttributeData,
              renderProps.path,
              displayValues
            )}
          />
        );
      },
    };
  },

  getCustomDataSourceFieldDefinition(renderProps) {
    return {
      defaultLabel: renderProps.label,
      displayType: 'multiOptions',
      dataType: 'textArray',
      getOptions: () =>
        renderProps.options?.map((o) => {
          if (o._tag === 'StringOption') {
            return { value: o.Value, label: o.Value };
          } else {
            return { value: o.AltValue, label: o.Value };
          }
        }) ?? [],
    };
  },
  hasOptions: true,
  hasAlternateLabel: true,
  allowAsConditionSource: true,
};
